/* eslint-disable no-unused-vars */
import React from 'react';
import {
  Checkbox,
  MenuItem,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';

import DayjsUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import dayjs from 'dayjs';
import InputDuration from 'components/atoms/InputDuration';
import { Autocomplete } from '@material-ui/lab';

import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import useStyles from './style';

type ISelectOption = {
  label: string;
  value: string | number;
};

type IOption = {
  label: string;
  value: string | number;
};

export const SelectOptionsEditorComponent = ({
  options,
  isLoading,
  error,
  value,
  onValueChange,
}: {
  options: ISelectOption[];
  isLoading: boolean;
  error: boolean;
  value: string | number | undefined;
  onValueChange(newValue: string | number): void;
}) => {
  const classes = useStyles();

  return (
    <TextField
      select
      error={error}
      variant="outlined"
      className={classes.defaultInputBGColor}
      value={value}
      onChange={e => onValueChange(e.target.value)}
      InputProps={{
        endAdornment: <LoadingInputEndAdornment loading={isLoading} />,
      }}
    >
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

const MultipleSelectOptionsEditor = ({
  options,
  error,
  value,
  isLoading,
  onValueChange,
}: {
  options: IOption[];
  error: boolean;
  value: (string | number)[];
  isLoading: boolean;
  onValueChange(newValue): void;
}) => {
  const classes = useStyles();

  const [itemValue, setItemValue] = React.useState<IOption[]>(() =>
    options.filter(item => value.includes(item.value)),
  );

  let autoCompleteClassName = classes.root;
  if (value)
    autoCompleteClassName = `${classes.root} ${classes.hasItemSelected}`;

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const hasMoreOneSelected = itemValue.length > 1;

  const getLimitTagsText = (more: number) =>
    `${more} ${hasMoreOneSelected ? 'Itens' : 'Item'}`;

  const inputRenderer = React.useCallback(
    (params: TextFieldProps) => (
      <TextField
        {...params}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={isLoading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [isLoading],
  );

  return (
    <Autocomplete
      className={autoCompleteClassName}
      id="group"
      multiple
      disableCloseOnSelect
      disableClearable
      limitTags={0}
      getLimitTagsText={getLimitTagsText}
      options={options}
      getOptionLabel={option => option.label}
      getOptionSelected={getOptionSelected}
      renderOption={(option, { selected }) => (
        <>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
            color="primary"
          />
          {option.label}
        </>
      )}
      onChange={(e, selected) => {
        setItemValue(selected);
      }}
      value={itemValue}
      onBlur={() => onValueChange(itemValue.map(item => item.value))}
      renderInput={params => {
        const extraProps = {
          name: 'group',
          error,
        };
        return inputRenderer({ ...params, ...extraProps });
      }}
    />
  );
};

export const MultipleSelectOptionsEditorComponent = React.memo(
  MultipleSelectOptionsEditor,
);

export const InputNumberEditorComponent = ({ error, value, onValueChange }) => {
  const classes = useStyles();
  const [actualValue, setActualValue] = React.useState<string | number>();

  return (
    <TextField
      variant="outlined"
      error={error}
      className={classes.defaultInputBGColor}
      inputProps={{
        type: 'number',
      }}
      onChange={e => setActualValue(Number(e.target.value))}
      onBlur={e => onValueChange(Number(e.target.value))}
      value={actualValue !== undefined ? actualValue : value}
    />
  );
};

export const InputTextEditorComponent = ({
  /**
   * Como a validação do textField é feita somente no onBlur,
   * o tamanho máximo de texto deve ser passado diretamente
   * para o component
   */
  maxLength = 0,
  error,
  value,
  onValueChange,
}) => {
  const classes = useStyles();
  const [actualValue, setActualValue] = React.useState<string>();

  const extraProps = { inputProps: {} };

  if (maxLength > 0) {
    extraProps.inputProps = { maxLength };
  }

  return (
    <TextField
      variant="outlined"
      error={error}
      className={classes.defaultInputBGColor}
      onChange={e => setActualValue(e.target.value)}
      onBlur={e => onValueChange(e.target.value)}
      value={actualValue !== undefined ? actualValue : value}
      {...extraProps}
    />
  );
};

export const DateEditorComponent = ({
  format,
  error,
  value,
  placeholder,
  onValueChange,
}: {
  format: string;
  error: boolean;
  value: string | undefined;
  placeholder: string | undefined;
  onValueChange(newValue: string | null | undefined): void;
}) => {
  const classes = useStyles();

  const updateDate = newDate => {
    const date = dayjs(newDate, format);
    if (date.isValid()) {
      onValueChange(date.toISOString());
    }

    if (!newDate) {
      onValueChange(null);
    }
  };

  return (
    <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
      <KeyboardDatePicker
        inputVariant="outlined"
        className={classes.defaultInputBGColor}
        format={format}
        cancelLabel="Cancelar"
        clearLabel="Limpar"
        error={error}
        clearable
        placeholder={placeholder}
        invalidDateMessage=""
        minDateMessage=""
        maxDateMessage=""
        inputProps={{
          onChange: e => {
            const target = e.target as HTMLInputElement;
            updateDate(target.value);
          },
        }}
        onChange={newValue => updateDate(newValue)}
        value={dayjs(value)}
      />
    </MuiPickersUtilsProvider>
  );
};

export const DateTimeEditorComponent = ({
  format,
  error,
  value,
  placeholder,
  onValueChange,
}: {
  format: string;
  error: boolean;
  value: string | undefined;
  placeholder: string | undefined;
  onValueChange(newValue: string | null | undefined): void;
}) => {
  const classes = useStyles();

  const updateDate = newDate => {
    const date = dayjs(newDate, format);
    if (date.isValid()) {
      onValueChange(date.toISOString());
    }

    if (!newDate) {
      onValueChange(null);
    }
  };

  return (
    <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
      <KeyboardDateTimePicker
        inputVariant="outlined"
        className={classes.defaultInputBGColor}
        format={format}
        cancelLabel="Cancelar"
        clearLabel="Limpar"
        error={error}
        clearable
        placeholder={placeholder}
        invalidDateMessage=""
        minDateMessage=""
        maxDateMessage=""
        inputProps={{
          onChange: e => {
            const target = e.target as HTMLInputElement;
            updateDate(target.value);
          },
        }}
        onChange={newValue => updateDate(newValue)}
        value={dayjs(value)}
      />
    </MuiPickersUtilsProvider>
  );
};

export const DurationEditorComponent = ({ value, onValueChange, error }) => {
  const classes = useStyles();

  let formatedValue = value || '';

  if (value) {
    formatedValue = dayjs.duration(+formatedValue, 'hours').format('HH:mm');
  }

  return (
    <TextField
      variant="outlined"
      className={classes.defaultInputBGColor}
      error={error}
      onBlur={e => {
        const targetValue = e.target.value;

        const [hours, minutes] = targetValue.split(':');
        const durationValue = dayjs
          .duration({
            hours: Number(hours),
            minutes: Number(minutes),
          })
          .asHours()
          .toFixed(2);

        onValueChange(durationValue);
      }}
      value={formatedValue}
      InputProps={{ inputComponent: InputDuration }}
    />
  );
};
